import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

export default function SalesByProduct() {
  const { user, companyId } = useAuth();
  const navigate = useNavigate();

  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [companySnap, setCompanySnap] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const [plan, setPlan] = useState('');
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('qty');
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [bundlesLoaded, setBundlesLoaded] = useState(false);

  // --------------------------
  // Load Plan
  // --------------------------
  const loadPlan = async () => {
    const docRef = doc(db, 'companies', companyId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      setPlan(docSnap.data()?.subscription?.plan || 'Free Trial');
    }
  };

  // --------------------------
  // Load Products
  // --------------------------
  const loadProducts = async () => {
    if (!user || !companyId) return;
    const snap = await getDocs(
      collection(db, 'companies', companyId, 'inventory', 'data', 'products')
    );
    setProducts(snap.docs.map(doc => ({ ...doc.data(), type: 'product' })));
    setProductsLoaded(true);
  };

  // --------------------------
  // Load Bundles
  // --------------------------
  const loadBundles = async () => {
    if (!user || !companyId) return;
    const snap = await getDocs(
      collection(db, 'companies', companyId, 'inventory', 'data', 'bundles')
    );
    setBundles(snap.docs.map(doc => ({ ...doc.data(), type: 'bundle' })));
    setBundlesLoaded(true);
  };

  // --------------------------
  // Load Company Info
  // --------------------------
  const loadCompany = async () => {
    if (!user || !companyId) return;
    const snap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
    if (snap.exists()) setCompanySnap(snap.data());
  };

  // --------------------------
  // Load Sales
  // --------------------------
  const formatVariantLabel = (item, matchedProduct) => {
    if (item.variantName) return item.variantName;
    const variantId = item.variantId || item.variant_id;
    if (variantId && matchedProduct?.variants) {
      const v = matchedProduct.variants.find(
        (opt) => opt.id === variantId || opt.variantId === variantId
      );
      if (!v) return '';
      const optionsLabel =
        Array.isArray(v.options) && v.options.length > 0
          ? v.options.map(o => `${o.group}: ${o.value}`).join(' / ')
          : v.name || v.label || '';
      return optionsLabel;
    }
    return '';
  };

  const loadSales = async () => {
    if (!user || !companyId) return;
  

    const q = query(
      collection(db, 'companies', companyId, 'orders'),
      where('paymentStatus', '!=', 'Void'),
      orderBy('paymentStatus'),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);

    const allOrders = snapshot.docs.map(doc => doc.data());
    const allItems = allOrders.flatMap(order => {
      return order.items.map(item => {
        const isBundle = item.type === 'bundle' || bundles.some(b => b.name === item.name);
        const matchedProduct = isBundle
          ? bundles.find(p => p.name === item.name)
          : products.find(p => p.name === item.name);

        const variantLabel = isBundle ? '' : formatVariantLabel(item, matchedProduct);
        const baseName = item.name || matchedProduct?.name || 'Unnamed';
        const displayName = isBundle ? `${baseName} (Bundle)` : baseName;

        return {
          name: displayName,
          baseName,
          variantLabel,
          sku: isBundle ? 'BUNDLE' : (matchedProduct?.sku || '-'),
          qty: Number(item.qty || item.quantity || 0),
          total: Number(item.qty || item.quantity || 0) * Number(item.price || 0),
          timestamp: order.timestamp,
          type: isBundle ? 'bundle' : 'product'
        };
      });
    });

    setSales(allItems);
  };

  // --------------------------
  // Initial Load
  // --------------------------
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
    loadPlan();
  }, []);

  useEffect(() => {
    const loadAllData = async () => {
      if (!user || !companyId) return;
      setLoading(true);

      await Promise.all([loadProducts(), loadBundles(), loadCompany()]);
      setLoading(false);
    };
    loadAllData();
  }, [user, companyId]);

  useEffect(() => {
    if (user && companyId && productsLoaded && bundlesLoaded) {
      
      
      loadSales();
    } else {
      
    }
  }, [user, companyId, productsLoaded, bundlesLoaded]);

  // --------------------------
  // Filter + Sort
  // --------------------------
  const filtered = sales.filter(item => {
    const itemDate = new Date(item.timestamp);
    const from = fromDate ? new Date(fromDate) : new Date(0);
    const to = toDate ? new Date(toDate) : new Date();
    to.setHours(23, 59, 59, 999);

    const matchSearch =
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      (item.baseName?.toLowerCase().includes(search.toLowerCase())) ||
      (item.variantLabel?.toLowerCase().includes(search.toLowerCase())) ||
      (item.sku?.toLowerCase().includes(search.toLowerCase()));
    const matchDate = itemDate >= from && itemDate <= to;
    return matchSearch && matchDate;
  });

  let grouped = Object.values(
    filtered.reduce((acc, item) => {
      const key = `${item.baseName}|${item.sku}|${item.variantLabel || ''}|${item.type}`;
      if (!acc[key]) {
        acc[key] = {
          name: item.name,
          variantLabel: item.variantLabel,
          sku: item.sku,
          qty: 0,
          total: 0,
        };
      }
      acc[key].qty += item.qty;
      acc[key].total += item.total;
      return acc;
    }, {})
  );

  grouped.sort((a, b) => (sortBy === 'qty' ? b.qty - a.qty : b.total - a.total));

  const paginated = grouped.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.max(1, Math.ceil(grouped.length / perPage));

  const chartTop = useMemo(() => grouped.slice(0, 10), [grouped]);
  const chartData = useMemo(() => {
    const labels = chartTop.map(item =>
      item.variantLabel ? `${item.name} — ${item.variantLabel}` : item.name
    );
    const dataVals = chartTop.map(item => (sortBy === 'qty' ? item.qty : item.total));
    return {
      labels,
      datasets: [
        {
          label: sortBy === 'qty' ? 'Quantity Sold' : 'Total Sales (RM)',
          data: dataVals,
          backgroundColor: '#8b5cf6',
          borderColor: '#a78bfa',
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [chartTop, sortBy]);

  const chartOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed.x || 0;
            return sortBy === 'qty' ? `Qty: ${val}` : `RM ${Number(val).toFixed(2)}`;
          },
        },
        displayColors: false,
        titleColor: '#0b0f19',
        bodyColor: '#0b0f19',
        backgroundColor: '#f9fafb',
        borderColor: '#0b0f19',
        borderWidth: 1,
      },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: {
          color: '#e5e7eb',
          callback: (val) => (sortBy === 'qty' ? val : `RM ${Number(val).toFixed(0)}`),
        },
        grid: { color: 'rgba(229,231,235,0.1)' },
      },
      y: {
        ticks: { color: '#e5e7eb', autoSkip: false },
        grid: { color: 'rgba(229,231,235,0.05)' },
      },
    },
  }), [sortBy]);

  // --------------------------
  // Export PDF
  // --------------------------
  const exportToPDF = () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }

    const docPDF = new jsPDF('p', 'mm', 'a4');
    const formatDate = dateStr =>
      dateStr ? new Date(dateStr).toLocaleDateString('en-GB') : 'All';

    const fromFormatted = formatDate(fromDate);
    const toFormatted = formatDate(toDate);
    const title = `Sales By Product Report (${fromFormatted} - ${toFormatted})`;

    docPDF.setFontSize(12);
    docPDF.text(companySnap.companyName || 'Company Name', 105, 10, { align: 'center' });
    docPDF.setFontSize(10);
    docPDF.text(title, 105, 16, { align: 'center' });

    // Add chart image if available
    try {
      const chartCanvas = document.querySelector('.sales-by-product-chart canvas');
      if (chartCanvas) {
        const imgData = chartCanvas.toDataURL('image/png', 1.0);
        const pageWidth = docPDF.internal.pageSize.getWidth() - 20;
        const imgHeight = 80;
        docPDF.addImage(imgData, 'PNG', 10, 20, pageWidth, imgHeight);
      }
    } catch (err) {
      console.warn('Could not render chart to PDF:', err);
    }

    autoTable(docPDF, {
      head: [['Product', 'SKU', 'Quantity Sold', 'Total Sales']],
      body: grouped.map(item => [
        `${item.name}${item.variantLabel ? `\nVariant: ${item.variantLabel}` : ''}`,
        item.sku,
        item.qty,
        `RM${item.total.toFixed(2)}`
      ]),
      startY: 105,
      styles: { fontSize: 9 }
    });

    const pageHeight = docPDF.internal.pageSize.height;
    docPDF.setFontSize(9);
    docPDF.text('Powered by CashMate', 105, pageHeight - 10, { align: 'center' });

    const cleanFilename = `sales-by-product_${fromFormatted.replace(/\//g, '-')}_to_${toFormatted.replace(/\//g, '-')}.pdf`;
    docPDF.save(cleanFilename);
  };

  // --------------------------
  // Loading State
  // --------------------------
  if (loading) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading information...</span>
      </div>
    );
  }

  // --------------------------
  // Render
  // --------------------------
  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button
          onClick={() => navigate(-1)}
          className="text-sm flex items-center gap-1 text-gray-300 hover:text-white"
        >
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Sales By Product</h2>

      <div className="flex gap-4 mb-6">
        <div className="flex gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
          />
        </div>
        <input
          type="text"
          placeholder="Search by name or SKU"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
        />
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
        >
          <option value="qty">Sort by Quantity Sold (High → Low)</option>
          <option value="total">Sort by Total Sales (High → Low)</option>
        </select>
        <button
          onClick={exportToPDF}
          className="px-3 py-2 bg-purple-600 rounded text-white"
        >
          Export to PDF
        </button>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg p-4 mb-6 sales-by-product-chart">
        <h3 className="text-sm text-gray-300 mb-3">
          Top 10 Products by {sortBy === 'qty' ? 'Quantity Sold' : 'Total Sales (RM)'}
        </h3>
        <div className="h-96 w-full">
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="px-4 py-2">SKU</th>
              <th className="px-4 py-2">Product</th>
              <th className="px-4 py-2">Quantity</th>
              <th className="px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((item, i) => (
              <tr key={i} className="border-t border-gray-700">
                <td className="px-4 py-2">{item.sku || '-'}</td>
                <td className="px-4 py-2">
                  <div className="text-white">{item.name}</div>
                  {item.variantLabel && (
                    <div className="text-xs text-gray-400">Variant: {item.variantLabel}</div>
                  )}
                </td>
                <td className="px-4 py-2">{item.qty}</td>
                <td className="px-4 py-2 font-bold">RM {item.total.toFixed(2)}</td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-400" colSpan={4}>
                  No data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center flex-wrap mt-6 gap-2">
        <button
          className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(p => p - 1)}
        >
          Prev
        </button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`px-4 py-2 rounded ${
              currentPage === i + 1
                ? 'bg-purple-600 text-white'
                : 'bg-[#2c2c2c] text-gray-300'
            }`}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}
        <button
          className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(p => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
